import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMercadoPago } from '../hooks/useMercadoPago';
import { buildApiUrl } from '../services/api';
import { useGlobalSettings } from '../hooks/useGlobalSettings';
import { AppliedCoupon, createEmptyPaymentFormData, PaymentFormData } from '../types/payment';
import {
  getCouponDiscountPercent,
  getCouponFreeDays,
  getCouponSummaryText,
  isFreeDaysCoupon,
  normalizeCouponCode,
  parseCouponValidationResponse
} from '../utils/couponUtils';
import { maskCPF } from '../utils/documentUtils';

const isValidCPF = (value: string): boolean => {
  const digits = value.replace(/\D/g, '');

  if (digits.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  const numbers = digits.split('').map(Number);

  const firstSum = numbers
    .slice(0, 9)
    .reduce((sum, num, index) => sum + num * (10 - index), 0);
  let firstCheck = (firstSum * 10) % 11;
  if (firstCheck === 10) firstCheck = 0;
  if (firstCheck !== numbers[9]) return false;

  const secondSum = numbers
    .slice(0, 10)
    .reduce((sum, num, index) => sum + num * (11 - index), 0);
  let secondCheck = (secondSum * 10) % 11;
  if (secondCheck === 10) secondCheck = 0;

  return secondCheck === numbers[10];
};

const registerSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('E-mail inválido'),
  document: z.string().min(11, 'CPF é obrigatório').refine(isValidCPF, 'CPF inválido'),
  registroNumero: z.string().min(2, 'Número de registro é obrigatório'),
  orgaoEmissor: z.string().min(2, 'Órgão emissor é obrigatório'),
  senha: z.string()
    .min(6, 'Senha deve ter pelo menos 6 caracteres')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Senha deve conter pelo menos uma letra minúscula, uma maiúscula e um número'),
  confirmSenha: z.string().min(6, 'Confirmação de senha é obrigatória'),
}).refine((data) => data.senha === data.confirmSenha, {
  path: ['confirmSenha'],
  message: 'As senhas não conferem'
});

type RegisterFormData = z.infer<typeof registerSchema>;

export function RegisterPage() {
  const maxInstallments = 1;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [pendingData, setPendingData] = useState<RegisterFormData | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [isEncryptingCard, setIsEncryptingCard] = useState(false);
  const [paymentData, setPaymentData] = useState<PaymentFormData>(createEmptyPaymentFormData);
  const [paymentErrors, setPaymentErrors] = useState<Record<string, string>>({});
  const [isCepLoading, setIsCepLoading] = useState(false);
  const [lastCepLookup, setLastCepLookup] = useState('');
  const navigate = useNavigate();
  const { settings } = useGlobalSettings();
  const { createCardToken } = useMercadoPago();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      nome: '',
      email: '',
      document: '',
      registroNumero: '',
      orgaoEmissor: '',
      senha: '',
      confirmSenha: '',
    },
  });
  const baseInstallmentCents = 5990;
  const baseTotalCents = baseInstallmentCents;
  const appliedDiscountPercent = getCouponDiscountPercent(appliedCoupon);
  const hasDiscountPercent = appliedDiscountPercent > 0;
  const isTrialCoupon = isFreeDaysCoupon(appliedCoupon);
  const trialDays = getCouponFreeDays(appliedCoupon);
  const requiresImmediatePayment = !isTrialCoupon;
  const discountMultiplier = 1 - appliedDiscountPercent / 100;
  const getPricingForInstallments = (installmentsInput: number) => {
    const installments = Math.min(maxInstallments, Math.max(1, installmentsInput));
    const discountedTotalCents = Math.max(0, Math.round(baseTotalCents * discountMultiplier));
    const discountedInstallmentCents = discountedTotalCents;
    return {
      installments,
      discountedTotalCents,
      discountedInstallmentCents,
      oneTimeDiscountAppliedPercent: 0
    };
  };
  const selectedPricing = getPricingForInstallments(maxInstallments);
  const safeInstallments = selectedPricing.installments;
  const discountedInstallmentCents = selectedPricing.discountedInstallmentCents;
  const originalTotalCents = baseTotalCents;
  const originalInstallmentCents = Math.max(0, Math.round(originalTotalCents / safeInstallments));
  const formatCurrency = (amountCents: number) => (
    new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amountCents / 100)
  );
  const installmentAmount = formatCurrency(discountedInstallmentCents);
  const originalInstallmentAmount = formatCurrency(originalInstallmentCents);
  const hasAnyDiscount = hasDiscountPercent;

  useEffect(() => {
    if (!showTermsModal && !showPaymentModal) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [showTermsModal, showPaymentModal]);

  useEffect(() => {
    const cepDigits = paymentData.billingZip.replace(/\D/g, '');

    if (cepDigits.length !== 8 || cepDigits === lastCepLookup) {
      return;
    }

    let isActive = true;
    const timer = setTimeout(async () => {
      setIsCepLoading(true);

      try {
        const response = await fetch(buildApiUrl(`/cep/${cepDigits}`));
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          if (isActive) {
            setPaymentErrors((prev) => ({
              ...prev,
              billingZip: data?.error || 'CEP não encontrado'
            }));
            setLastCepLookup(cepDigits);
          }
          return;
        }

        if (!isActive || !data) {
          return;
        }

        setPaymentData((prev) => ({
          ...prev,
          billingStreet: prev.billingStreet || data.street || '',
          billingDistrict: prev.billingDistrict || data.district || '',
          billingCity: prev.billingCity || data.city || '',
          billingState: prev.billingState || data.state || '',
          billingComplement: prev.billingComplement || data.complement || ''
        }));

        setPaymentErrors((prev) => {
          const next = { ...prev };
          delete next.billingZip;
          delete next.billingStreet;
          delete next.billingDistrict;
          delete next.billingCity;
          delete next.billingState;
          return next;
        });
        setLastCepLookup(cepDigits);
      } catch (error) {
        if (isActive) {
          setPaymentErrors((prev) => ({
            ...prev,
            billingZip: 'Erro ao consultar CEP'
          }));
          setLastCepLookup(cepDigits);
        }
      } finally {
        if (isActive) {
          setIsCepLoading(false);
        }
      }
    }, 350);

    return () => {
      isActive = false;
      clearTimeout(timer);
    };
  }, [paymentData.billingZip, lastCepLookup]);

  const termsText = `TERMO DE USO E CONCORDÂNCIA DO APLICATIVO LEADNEXIA

Pelo presente instrumento particular, de um lado VIA BOOTS SOLUÇÕES EM TECNOLOGIA LTDA, pessoa jurídica de direito privado, CNPJ 61.438.361/0001-09, doravante denominada LICENCIANTE, desenvolvedora e mantenedora do aplicativo LEADNEXIA, e, de outro lado, o USUÁRIO, devidamente identificado no momento do cadastro, ajustam entre si o presente TERMO DE USO, que se regerá pelas cláusulas e condições a seguir.

CLÁUSULA 1 – DO OBJETO

1.1. O presente Termo tem por objeto regular as condições de uso não exclusivo, pessoal e intransferível do aplicativo LEADNEXIA, destinado exclusivamente a oficiais de justiça, com a finalidade de auxiliar na organização, no registro e no apoio ao cumprimento de mandados judiciais, por meio de ferramentas digitais.

1.2. O LEADNEXIA constitui ferramenta de apoio, não substituindo sistemas oficiais do Poder Judiciário, nem alterando, suprimindo ou ampliando as atribuições legais do usuário.

CLÁUSULA 2 – DA NATUREZA DO USO

2.1. O presente instrumento trata-se exclusivamente de contrato de uso, não estabelecendo qualquer vínculo empregatício, funcional, associativo, societário ou institucional entre o usuário, o LEADNEXIA, a Via Boots Soluções em Tecnologia LTDA ou o Poder Judiciário.

2.2. O uso do aplicativo é realizado por conta e risco exclusivo do usuário, que permanece integralmente responsável por seus atos, registros, comunicações e pelo cumprimento das normas legais, regulamentares e administrativas aplicáveis à sua atividade.

CLÁUSULA 3 – DAS RESPONSABILIDADES DO USUÁRIO

3.1. O usuário declara, sob sua exclusiva responsabilidade, que:

I – Utilizará o aplicativo de forma ética, lícita e compatível com suas atividades;
II – É integralmente responsável pela veracidade das informações inseridas no sistema.

3.2. O usuário compromete-se a:

I – Não utilizar o aplicativo para fins ilícitos, indevidos ou contrários às normas de sua atividade;
II – Manter o sigilo sobre informações sensíveis, dados pessoais e conteúdos protegidos;
III – Zelar pela segurança de suas credenciais de acesso (login e senha), respondendo por qualquer uso indevido decorrente de sua negligência.

CLÁUSULA 4 – DAS LIMITAÇÕES DE RESPONSABILIDADE

4.1. O LEADNEXIA e a Via Boots Soluções em Tecnologia LTDA não se responsabilizam, em nenhuma hipótese:

I – Pelo uso inadequado, indevido ou em desconformidade com este Termo;
II – Por falhas decorrentes de conexão à internet, dispositivos do usuário ou serviços de terceiros.

4.2. O LEADNEXIA não se responsabiliza por regras, políticas, termos ou normas estipuladas pela empresa Meta Platforms, Inc. (Meta), incluindo, mas não se limitando, a políticas anti-spam, restrições de envio de mensagens, bloqueios, suspensões ou limitações impostas por seus serviços, tais como WhatsApp, Instagram ou Facebook.

4.3. O usuário declara estar ciente de que qualquer penalidade aplicada por terceiros não gera responsabilidade solidária ou subsidiária da Via Boots Soluções em Tecnologia LTDA.

CLÁUSULA 5 – DA VIGÊNCIA E RENOVAÇÃO

5.1. O presente Termo é celebrado por prazo determinado, conforme o plano ou modalidade de assinatura contratada pelo usuário.

5.2. O contrato será renovado automaticamente por igual período ao término de sua vigência, salvo manifestação contrária do usuário, a ser realizada com antecedência mínima de 10 (dez) dias antes do vencimento.

5.3. A manifestação contrária deverá ocorrer por meio das funcionalidades disponíveis no aplicativo ou por canal oficial indicado pela Via Boots Soluções em Tecnologia LTDA.

CLÁUSULA 6 – DA RESCISÃO, CANCELAMENTO E RESTITUIÇÃO

6.1. O presente Termo poderá ser rescindido:

I – A qualquer tempo, por iniciativa do usuário;
II – Pela Via Boots Soluções, em caso de violação das disposições deste Termo;
III – Por descontinuidade do aplicativo, mediante aviso prévio razoável.

6.2. A rescisão não gera direito a indenização de qualquer natureza, ressalvadas as hipóteses expressamente previstas neste Termo.

6.3. No caso de cancelamento do contrato de assinatura anual, será respeitado o prazo legal de arrependimento, nos termos da legislação vigente, hipótese em que será assegurada a restituição integral dos valores pagos, observadas as regras do meio de pagamento utilizado.

6.4. Decorrido o prazo legal de cancelamento, o pedido de rescisão poderá ser realizado a qualquer tempo; contudo, não haverá restituição, total ou parcial, dos valores pagos, permanecendo o acesso ao serviço ativo até o término do período contratado, salvo disposição expressa em contrário.

6.5. O usuário declara estar ciente de que a política de cancelamento ora estabelecida é compatível com a natureza do serviço prestado e com a disponibilização contínua da plataforma durante o período contratado.

CLÁUSULA 7 – DA PROTEÇÃO DE DADOS

7.1. O tratamento de dados pessoais observará integralmente a Lei Geral de Proteção de Dados – LGPD (Lei nº 13.709/2018).

7.2. Os dados coletados serão utilizados exclusivamente para:

I – Funcionamento do aplicativo;
II – Melhoria contínua dos serviços;
III – Cumprimento de obrigações legais e regulatórias.

CLÁUSULA 8 – DA PROPRIEDADE INTELECTUAL

8.1. O aplicativo LEADNEXIA, suas funcionalidades, layout, marca, código-fonte e demais elementos são de propriedade exclusiva da ViaBoots Soluções em Tecnologia LTDA, sendo vedada qualquer reprodução, modificação, engenharia reversa ou uso não autorizado.

CLÁUSULA 9 – DAS DISPOSIÇÕES GERAIS

9.1. A eventual tolerância de qualquer das partes quanto ao descumprimento de cláusulas não implicará novação ou renúncia de direitos.

9.2. Caso qualquer disposição deste Termo seja considerada inválida ou inexequível, as demais permanecerão em pleno vigor.

CLÁUSULA 10 – DO FORO

10.1. Para dirimir quaisquer controvérsias oriundas deste Termo, fica eleito o foro da comarca de domicílio da Via Boots Soluções em Tecnologia LTDA, com renúncia expressa a qualquer outro, por mais privilegiado que seja.

ACEITE


Ao clicar em “LI E CONCORDO COM OS TERMOS DE USO”, o usuário declara que leu, compreendeu e concorda integralmente com todas as cláusulas deste Termo.`;

  const submitRegistration = async (data: RegisterFormData, cardTokenId?: string) => {
    setIsSubmitting(true);
    let wasSuccessful = false;
    try {
      const formData = new FormData();
      formData.append('nome', data.nome);
      formData.append('email', data.email);
      formData.append('document', data.document);
      formData.append('senha', data.senha);
      formData.append('registroNumero', data.registroNumero);
      formData.append('orgaoEmissor', data.orgaoEmissor);
      formData.append('termsAccepted', 'true');
      formData.append('installments', String(safeInstallments));
      if (cardTokenId) {
        formData.append('holderName', paymentData.holderName);
        formData.append('holderDocument', paymentData.holderDocument);
        formData.append('billingZip', paymentData.billingZip);
        formData.append('billingStreet', paymentData.billingStreet);
        formData.append('billingNumber', paymentData.billingNumber);
        formData.append('billingComplement', paymentData.billingComplement);
        formData.append('billingDistrict', paymentData.billingDistrict);
        formData.append('billingCity', paymentData.billingCity);
        formData.append('billingState', paymentData.billingState);
        formData.append('cardTokenId', cardTokenId);
      }
      if (appliedCoupon?.code) {
        formData.append('couponCode', appliedCoupon.code);
      }

      const response = await fetch(buildApiUrl('/auth/register-public'), {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Erro ao criar conta');
      }

      toast.success(result.message || 'Conta criada com sucesso!');
      navigate('/login');
      wasSuccessful = true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao criar conta';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
    return wasSuccessful;
  };

  const handleApplyCoupon = async () => {
    const normalized = normalizeCouponCode(couponCode);
    if (!normalized) {
      setCouponError('Informe um cupom válido');
      return;
    }

    setIsApplyingCoupon(true);
    setCouponError('');

    try {
      const response = await fetch(buildApiUrl('/coupons/validate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: normalized })
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.message || data?.error || 'Cupom inválido');
      }

      const parsedCoupon = parseCouponValidationResponse(data, normalized);
      setAppliedCoupon(parsedCoupon);
      setCouponCode(normalized);
      setCouponError('');

      if (isFreeDaysCoupon(parsedCoupon)) {
        setPaymentErrors({});
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao validar cupom';
      setCouponError(message);
      setAppliedCoupon(null);
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  const onSubmit = async (data: RegisterFormData) => {
    setPendingData(data);
    setTermsAccepted(false);
    setShowTermsModal(true);
  };

  const confirmTermsAndSubmit = async () => {
    if (!pendingData) {
      return;
    }

    setShowTermsModal(false);
    setShowPaymentModal(true);
  };

  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 19);
    return digits.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  };

  const formatCep = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  };

  const validatePayment = () => {
    if (isTrialCoupon) {
      setPaymentErrors({});
      return true;
    }

    const nextErrors: Record<string, string> = {};
    const cardDigits = paymentData.cardNumber.replace(/\D/g, '');
    const cepDigits = paymentData.billingZip.replace(/\D/g, '');

    if (!paymentData.holderName.trim()) nextErrors.holderName = 'Nome do titular é obrigatório';
    if (!isValidCPF(paymentData.holderDocument)) nextErrors.holderDocument = 'CPF do titular inválido';
    if (cardDigits.length < 13) nextErrors.cardNumber = 'Número do cartão inválido';
    if (!/^\d{2}\/\d{2}$/.test(paymentData.cardExpiry)) nextErrors.cardExpiry = 'Validade inválida';
    if (!/^\d{3,4}$/.test(paymentData.cardCvv)) nextErrors.cardCvv = 'CVV inválido';
    if (cepDigits.length < 8) nextErrors.billingZip = 'CEP inválido';
    if (!paymentData.billingStreet.trim()) nextErrors.billingStreet = 'Endereço é obrigatório';
    if (!paymentData.billingNumber.trim()) nextErrors.billingNumber = 'Número é obrigatório';
    if (!paymentData.billingDistrict.trim()) nextErrors.billingDistrict = 'Bairro é obrigatório';
    if (!paymentData.billingCity.trim()) nextErrors.billingCity = 'Cidade é obrigatória';
    if (!/^[A-Za-z]{2}$/.test(paymentData.billingState.trim())) nextErrors.billingState = 'UF inválida';

    setPaymentErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handlePaymentSubmit = async () => {
    if (!pendingData) {
      return;
    }

    const skipImmediatePayment = isTrialCoupon;

    if (!skipImmediatePayment && !validatePayment()) {
      return;
    }

    let cardTokenId: string | undefined;
    if (!skipImmediatePayment) {
      try {
        setIsEncryptingCard(true);
        cardTokenId = await createCardToken(paymentData);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erro ao tokenizar cartão';
        toast.error(message);
        return;
      } finally {
        setIsEncryptingCard(false);
      }
    }

    const wasSuccessful = await submitRegistration(pendingData, cardTokenId);
    if (!wasSuccessful) {
      return;
    }

    setShowPaymentModal(false);
    setPendingData(null);
    setPaymentData(createEmptyPaymentFormData());
    setPaymentErrors({});
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            <div className="lg:w-1/2 p-12 flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg, #233e4f 0%, #1a2d3b 100%)' }}>
              <div className="text-center">
                <div className="w-24 h-24 bg-white/20 rounded-2xl mx-auto mb-8 flex items-center justify-center backdrop-blur-sm">
                  {settings?.iconUrl ? (
                    <img
                      src={settings.iconUrl}
                      alt="Ícone do Sistema"
                      className="w-16 h-16 object-contain"
                      style={{ filter: 'brightness(0) invert(1)' }}
                    />
                  ) : (
                    <img
                      src="/assets/default-logo.png"
                      alt="Vox Campaign"
                      className="w-16 h-16 object-contain"
                      style={{ filter: 'brightness(0) invert(1)' }}
                    />
                  )}
                </div>
                <h1 className="text-4xl font-bold mb-4">
                  {settings?.pageTitle || 'Vox Campaign'}
                </h1>
                <p className="text-gray-200 max-w-md">
                  Crie sua conta e organize suas campanhas com mais eficiência.
                </p>
              </div>
            </div>

            <div className="lg:w-1/2 p-12">
              <div className="max-w-md mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Cadastro</h2>
                  <p className="text-gray-600">Preencha seus dados para criar a conta</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div>
                  <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                    Nome completo
                  </label>
                  <input
                    {...register('nome')}
                    type="text"
                    id="nome"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                    style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                    onFocus={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.style.outline = 'none';
                      target.style.borderColor = '#233e4f';
                      target.style.boxShadow = '0 0 0 2px rgba(35, 62, 79, 0.2)';
                    }}
                    onBlur={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.style.borderColor = '#d1d5db';
                      target.style.boxShadow = 'none';
                    }}
                    placeholder="Seu nome completo"
                    disabled={isSubmitting}
                  />
                  {errors.nome && (
                    <p className="mt-2 text-sm text-red-600">{errors.nome.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    E-mail
                  </label>
                  <input
                    {...register('email')}
                    type="email"
                    id="email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                    style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                    onFocus={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.style.outline = 'none';
                      target.style.borderColor = '#233e4f';
                      target.style.boxShadow = '0 0 0 2px rgba(35, 62, 79, 0.2)';
                    }}
                    onBlur={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.style.borderColor = '#d1d5db';
                      target.style.boxShadow = 'none';
                    }}
                    placeholder="seu@email.com"
                    disabled={isSubmitting}
                  />
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="document" className="block text-sm font-medium text-gray-700 mb-2">
                    CPF
                  </label>
                  <input
                    {...register('document', {
                      onChange: (e) => setValue('document', maskCPF(e.target.value)),
                    })}
                    type="text"
                    id="document"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                    style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                    onFocus={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.style.outline = 'none';
                      target.style.borderColor = '#233e4f';
                      target.style.boxShadow = '0 0 0 2px rgba(35, 62, 79, 0.2)';
                    }}
                    onBlur={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.style.borderColor = '#d1d5db';
                      target.style.boxShadow = 'none';
                    }}
                    placeholder="Apenas números"
                    disabled={isSubmitting}
                  />
                  {errors.document && (
                    <p className="mt-2 text-sm text-red-600">{errors.document.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="registroNumero" className="block text-sm font-medium text-gray-700 mb-2">
                      Número de CRECI
                    </label>
                    <input
                      {...register('registroNumero')}
                      type="text"
                      id="registroNumero"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      onFocus={(e) => {
                        const target = e.target as HTMLInputElement;
                        target.style.outline = 'none';
                        target.style.borderColor = '#233e4f';
                        target.style.boxShadow = '0 0 0 2px rgba(35, 62, 79, 0.2)';
                      }}
                      onBlur={(e) => {
                        const target = e.target as HTMLInputElement;
                        target.style.borderColor = '#d1d5db';
                        target.style.boxShadow = 'none';
                      }}
                      placeholder="Ex: 12345"
                      disabled={isSubmitting}
                    />
                    {errors.registroNumero && (
                      <p className="mt-2 text-sm text-red-600">{errors.registroNumero.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="orgaoEmissor" className="block text-sm font-medium text-gray-700 mb-2">
                      Região
                    </label>
                    <input
                      {...register('orgaoEmissor')}
                      type="text"
                      id="orgaoEmissor"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      onFocus={(e) => {
                        const target = e.target as HTMLInputElement;
                        target.style.outline = 'none';
                        target.style.borderColor = '#233e4f';
                        target.style.boxShadow = '0 0 0 2px rgba(35, 62, 79, 0.2)';
                      }}
                      onBlur={(e) => {
                        const target = e.target as HTMLInputElement;
                        target.style.borderColor = '#d1d5db';
                        target.style.boxShadow = 'none';
                      }}
                      placeholder="Ex: DF"
                      disabled={isSubmitting}
                    />
                    {errors.orgaoEmissor && (
                      <p className="mt-2 text-sm text-red-600">{errors.orgaoEmissor.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="senha" className="block text-sm font-medium text-gray-700 mb-2">
                    Senha
                  </label>
                  <div className="relative">
                    <input
                      {...register('senha')}
                      type={showPassword ? 'text' : 'password'}
                      id="senha"
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      onFocus={(e) => {
                        const target = e.target as HTMLInputElement;
                        target.style.outline = 'none';
                        target.style.borderColor = '#233e4f';
                        target.style.boxShadow = '0 0 0 2px rgba(35, 62, 79, 0.2)';
                      }}
                      onBlur={(e) => {
                        const target = e.target as HTMLInputElement;
                        target.style.borderColor = '#d1d5db';
                        target.style.boxShadow = 'none';
                      }}
                      placeholder="Mín. 6 caracteres com maiúscula, minúscula e número"
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      disabled={isSubmitting}
                    >
                      {showPassword ? (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-4.477-10-10 0-1.038.158-2.04.452-2.981M6.228 6.228A9.969 9.969 0 0112 5c5.523 0 10 4.477 10 10 0 1.878-.518 3.634-1.42 5.13M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.senha && (
                    <p className="mt-2 text-sm text-red-600">{errors.senha.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmSenha" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmar senha
                  </label>
                  <div className="relative">
                    <input
                      {...register('confirmSenha')}
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmSenha"
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      onFocus={(e) => {
                        const target = e.target as HTMLInputElement;
                        target.style.outline = 'none';
                        target.style.borderColor = '#233e4f';
                        target.style.boxShadow = '0 0 0 2px rgba(35, 62, 79, 0.2)';
                      }}
                      onBlur={(e) => {
                        const target = e.target as HTMLInputElement;
                        target.style.borderColor = '#d1d5db';
                        target.style.boxShadow = 'none';
                      }}
                      placeholder="Repita a senha"
                      disabled={isSubmitting}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      disabled={isSubmitting}
                    >
                      {showConfirmPassword ? (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-4.477-10-10 0-1.038.158-2.04.452-2.981M6.228 6.228A9.969 9.969 0 0112 5c5.523 0 10 4.477 10 10 0 1.878-.518 3.634-1.42 5.13M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.confirmSenha && (
                    <p className="mt-2 text-sm text-red-600">{errors.confirmSenha.message}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full text-white py-3 px-6 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, #233e4f 0%, #1a2d3b 100%)',
                    '--tw-ring-color': '#233e4f'
                  } as React.CSSProperties}
                  onMouseEnter={(e) => {
                    const target = e.target as HTMLButtonElement;
                    target.style.background = 'linear-gradient(135deg, #1a2d3b 0%, #0f1a23 100%)';
                  }}
                  onMouseLeave={(e) => {
                    const target = e.target as HTMLButtonElement;
                    target.style.background = 'linear-gradient(135deg, #233e4f 0%, #1a2d3b 100%)';
                  }}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Criando conta...
                    </div>
                  ) : (
                    'Criar conta'
                  )}
                </button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    Já tem conta?{' '}
                    <Link
                      to="/login"
                      className="font-semibold text-gray-900 hover:text-gray-700 transition-colors"
                    >
                      Entrar
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showTermsModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6 sm:p-8">
        <div className="w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 text-sm">
            <h3 className="text-lg font-semibold text-gray-900">Termo de uso</h3>
            <button
              type="button"
              onClick={() => setShowTermsModal(false)}
              className="text-gray-500 transition-colors hover:text-gray-700"
              disabled={isSubmitting}
            >
              Fechar
            </button>
          </div>
          <div className="max-h-[60vh] overflow-y-auto px-6 py-4 text-sm text-gray-700 whitespace-pre-line">
            {termsText}
          </div>
          <div className="border-t border-gray-200 px-6 py-4">
            <label className="flex items-start gap-3 text-sm text-gray-800">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-2"
                style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                disabled={isSubmitting}
              />
              <span>LI E CONCORDO COM OS TERMOS DE USO</span>
            </label>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowTermsModal(false)}
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmTermsAndSubmit}
                disabled={!termsAccepted || isSubmitting}
                className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-60"
                style={{
                  background: 'linear-gradient(135deg, #233e4f 0%, #1a2d3b 100%)'
                }}
              >
                {isTrialCoupon ? 'Continuar' : 'Continuar para pagamento'}
              </button>
            </div>
          </div>
        </div>
      </div>
      )}
      {showPaymentModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col">
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {requiresImmediatePayment ? 'Pagamento' : 'Ativar dias grátis'}
              </h3>
              <p className="text-sm text-gray-500">
                {requiresImmediatePayment
                  ? 'Finalize sua assinatura com cartão de crédito.'
                  : `Seu cupom libera ${trialDays} dias grátis. O pagamento será solicitado depois.`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowPaymentModal(false)}
              className="text-gray-500 transition-colors hover:text-gray-700"
              disabled={isSubmitting}
            >
              Fechar
            </button>
          </div>
          <div className="px-6 py-6 flex-1 overflow-y-auto text-sm">
            <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
              <div className="space-y-5">
                <div>
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Dados do titular</h4>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome no cartão</label>
                  <input
                    type="text"
                    value={paymentData.holderName}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, holderName: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                    style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                    disabled={isSubmitting}
                  />
                  {paymentErrors.holderName && (
                    <p className="mt-2 text-sm text-red-600">{paymentErrors.holderName}</p>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CPF do titular</label>
                    <input
                      type="text"
                      value={paymentData.holderDocument}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, holderDocument: maskCPF(e.target.value) }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.holderDocument && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.holderDocument}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Número do cartão</label>
                    <input
                      type="text"
                      value={paymentData.cardNumber}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, cardNumber: formatCardNumber(e.target.value) }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.cardNumber && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.cardNumber}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Validade</label>
                    <input
                      type="text"
                      value={paymentData.cardExpiry}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, cardExpiry: formatExpiry(e.target.value) }))}
                      placeholder="MM/AA"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.cardExpiry && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.cardExpiry}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">CVV</label>
                    <input
                      type="password"
                      value={paymentData.cardCvv}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, cardCvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.cardCvv && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.cardCvv}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cobrança</label>
                    <div className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-900">
                      Mensal recorrente
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Endereço de cobrança</h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CEP
                      {isCepLoading && (
                        <span className="ml-2 text-xs font-medium text-gray-400">Buscando...</span>
                      )}
                    </label>
                    <input
                      type="text"
                      value={paymentData.billingZip}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, billingZip: formatCep(e.target.value) }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingZip && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingZip}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Endereço</label>
                    <input
                      type="text"
                      value={paymentData.billingStreet}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, billingStreet: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingStreet && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingStreet}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Número</label>
                    <input
                      type="text"
                      value={paymentData.billingNumber}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, billingNumber: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingNumber && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingNumber}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Complemento</label>
                    <input
                      type="text"
                      value={paymentData.billingComplement}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, billingComplement: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Bairro</label>
                    <input
                      type="text"
                      value={paymentData.billingDistrict}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, billingDistrict: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingDistrict && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingDistrict}</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Cidade</label>
                    <input
                      type="text"
                      value={paymentData.billingCity}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, billingCity: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingCity && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingCity}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">UF</label>
                    <input
                      type="text"
                      value={paymentData.billingState}
                      onChange={(e) => setPaymentData(prev => ({ ...prev, billingState: e.target.value.toUpperCase().slice(0, 2) }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting}
                    />
                    {paymentErrors.billingState && (
                      <p className="mt-2 text-sm text-red-600">{paymentErrors.billingState}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 space-y-4">
                <div className="rounded-xl bg-white p-4 shadow-sm">
                  <p className="text-sm text-gray-500">Plano mensal Vox Campaign</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    R$ {installmentAmount}/mês
                  </p>
                  {hasAnyDiscount && (
                    <p className="text-xs text-gray-400 line-through">
                      R$ {originalInstallmentAmount}/mês
                    </p>
                  )}
                  <p className="text-sm text-gray-500">Cobrança recorrente mensal</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
                  <label className="block text-sm font-medium text-gray-700">Cupom de desconto</label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => {
                        const nextValue = normalizeCouponCode(e.target.value);
                        setCouponCode(nextValue);
                        setCouponError('');
                        if (appliedCoupon && appliedCoupon.code !== nextValue) {
                          setAppliedCoupon(null);
                        }
                      }}
                      placeholder="Insira o cupom"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-colors"
                      style={{ '--tw-ring-color': '#233e4f' } as React.CSSProperties}
                      disabled={isSubmitting || isApplyingCoupon}
                    />
                    <button
                      type="button"
                      onClick={appliedCoupon ? handleRemoveCoupon : handleApplyCoupon}
                      disabled={isSubmitting || isApplyingCoupon || (!couponCode.trim() && !appliedCoupon)}
                      className="rounded-lg px-4 py-2 text-sm font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-60"
                      style={{ background: 'linear-gradient(135deg, #233e4f 0%, #1a2d3b 100%)' }}
                    >
                      {appliedCoupon ? 'Remover' : (isApplyingCoupon ? 'Validando...' : 'Aplicar')}
                    </button>
                  </div>
                  {couponError && (
                    <p className="text-sm text-red-600">{couponError}</p>
                  )}
                  {appliedCoupon && (
                    <p className="text-sm text-emerald-700">
                      {getCouponSummaryText(appliedCoupon)}
                    </p>
                  )}
                  {isTrialCoupon && (
                    <p className="text-xs text-emerald-700">
                      Você terá acesso imediato por {trialDays} dias. Depois disso, será necessário pagar.
                    </p>
                  )}
                </div>
                {requiresImmediatePayment ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    <strong>Importante:</strong> seu cadastro será ativado após a confirmação do pagamento.
                  </div>
                ) : (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
                    <strong>Dias grátis ativos:</strong> seu acesso será liberado imediatamente por {trialDays} dias.
                  </div>
                )}
                <div className="text-xs text-gray-500">
                  {requiresImmediatePayment
                    ? 'Ao continuar, você autoriza a cobrança recorrente mensal no cartão informado.'
                    : 'Quando os dias grátis terminarem, você verá uma tela interna para realizar o pagamento.'}
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-200 px-6 py-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowPaymentModal(false);
                  setShowTermsModal(true);
                }}
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                disabled={isSubmitting}
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={handlePaymentSubmit}
                disabled={isSubmitting || isEncryptingCard}
                className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-60"
                style={{
                  background: 'linear-gradient(135deg, #233e4f 0%, #1a2d3b 100%)'
                }}
              >
                {isEncryptingCard
                  ? 'Tokenizando cartão...'
                  : isSubmitting
                    ? 'Processando...'
                    : isTrialCoupon
                      ? `Concluir cadastro com ${trialDays} dias grátis`
                      : 'Confirmar pagamento e criar conta'}
              </button>
            </div>
          </div>
        </div>
      </div>
      )}
    </>
  );
}
